import React from 'react'

import {Box} from '@vio/atlas/helpers/Box'
import {IconName} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {ColorPath} from '@vio/atlas/types/Theme'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {OfferPaymentMode} from '@vio/core/src/offer/business/offerPaymentMode'

import {TextAlignedIcon} from './styles'

interface PaymentLabelProps {
  paymentType: OfferPaymentMode
  /** Applies sold-out styling to visually indicate that the offer is no longer available. */
  isOfferSoldOut?: boolean
}

const getColorPath = ({
  paymentMode,
  isOfferSoldOut
}: {
  paymentMode: OfferPaymentMode
  isOfferSoldOut: boolean
}): ColorPath => {
  if (isOfferSoldOut) {
    return 'content.neutral.disabled'
  }
  if (paymentMode === OfferPaymentMode.PayNow) {
    return 'content.neutral.primary'
  }
  return 'content.success.secondary'
}

const getPaymentIcon = (paymentMode: OfferPaymentMode): IconName => {
  switch (paymentMode) {
    case OfferPaymentMode.ChargeLater:
      return 'Clock'
    case OfferPaymentMode.PayAtProperty:
      return 'Wallet'
    case OfferPaymentMode.PayNow:
    default:
      return 'CreditCard'
  }
}

export const PaymentMessage = ({
  paymentMode
}: {
  paymentMode: OfferPaymentMode
}) => {
  switch (paymentMode) {
    case OfferPaymentMode.ChargeLater:
      return (
        <FormattedMessageWrapper
          id="payment.payLater"
          defaultMessage="Book now, pay later"
        />
      )
    case OfferPaymentMode.PayAtProperty:
      return (
        <FormattedMessageWrapper
          dataId="payLater"
          id="paymentSchedule.payAtProperty"
          defaultMessage="Pay at property"
        />
      )
    case OfferPaymentMode.PayNow:
    default:
      return (
        <FormattedMessageWrapper
          id="payment.payOnline"
          defaultMessage="Pay online"
        />
      )
  }
}

export const PaymentLabelType = ({
  paymentType,
  isOfferSoldOut = false
}: PaymentLabelProps) => {
  const colorPath = getColorPath({paymentMode: paymentType, isOfferSoldOut})
  const textVariant =
    paymentType === OfferPaymentMode.PayNow ? 'bodyS' : 'labelS'

  return (
    <Box
      display="flex"
      gap="s250"
      alignItems="baseline"
      data-id="payment-label"
    >
      <TextAlignedIcon
        name={getPaymentIcon(paymentType)}
        size="sm"
        colorPath={colorPath}
      />
      <Text variant={textVariant} colorPath={colorPath}>
        <PaymentMessage paymentMode={paymentType} />
      </Text>
    </Box>
  )
}
